import {ChangeDetectionStrategy, Component, computed, inject, resource, signal} from '@angular/core';
import {Button} from "primeng/button";
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {Message} from "primeng/message";
import {Skeleton} from "primeng/skeleton";
import {UserService} from "../../../services/user.service";
import {MessageService} from "primeng/api";
import {Clipboard} from "@capacitor/clipboard";
import {isNative} from "../../../utils";
import {HousingConnectAuthDto} from "homey-housing-lottery-app-dto";
import {PageSubtitleLayoutComponent} from "../../../components/page-subtitle-layout/page-subtitle-layout.component";
import {firstValueFrom} from "rxjs";
import {AnalyticsService} from "../../../services/analytics.service";

@Component({
  selector: 'homey-account-housing-connect',
  imports: [
    Button,
    PageTitleComponent,
    Message,
    Skeleton,
    PageSubtitleLayoutComponent
  ],
  templateUrl: './account-housing-connect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountHousingConnectComponent {
  private readonly userService = inject(UserService);
  private readonly messageService = inject(MessageService);
  private readonly analyticsService = inject(AnalyticsService);

  protected readonly passwordVisible = signal(false);

  protected readonly authResource = resource<HousingConnectAuthDto, void>({
    loader: async () => {
      try {
        return await firstValueFrom(this.userService.getHousingConnectAuth());
      } catch (error) {
        console.error('Failed to fetch Housing Connect credentials', error);
        throw error;
      }
    }
  });

  protected readonly auth = computed(() => this.authResource.value());
  protected readonly loading = this.authResource.isLoading;
  protected readonly hasError = computed(() => !!this.authResource.error());
  protected readonly hasAuth = computed(() => !!this.auth());
  protected readonly housingConnectUrl = 'https://housingconnect.nyc.gov/PublicWeb/';
  protected readonly user = this.userService.userAfterSignupFlow;

  protected togglePasswordVisibility(): void {
    this.passwordVisible.update(current => {
      const next = !current;
      if (next) {
        void this.analyticsService.sendEvent('housing-connect-password-got');
      }
      return next;
    });
  }

  protected async copy(field: 'username' | 'password'): Promise<void> {
    const auth = this.auth();
    if (!auth || !auth[field]) {
      return;
    }

    const text = auth[field];

    if (isNative) {
      await Clipboard.write({string: text});
    } else {
      await navigator.clipboard.writeText(text);
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Copied',
      detail: `${field === 'username' ? 'Username' : 'Password'} copied to clipboard`
    });

    void this.analyticsService.sendEvent('housing-connect-password-got', {field});
  }

  protected getMaskedPassword(): string {
    const auth = this.auth();
    if (!auth?.password) {
      return '********';
    }

    return '*'.repeat(Math.max(auth.password.length, 8));
  }
}
